'use client';
import { jsx, jsxs } from 'react/jsx-runtime';
import { flexRender } from '@tanstack/react-table';
import { useCallback } from 'react';
import styled, { css } from 'styled-components';
import { t } from '../../helpers/translate.js';
import { withTitan } from '../../hocs/withTitan/withTitan.js';
import { textStyle } from '../../mixins/textStyle.js';
import '../Icons/AlignStartHorizontalIcon.js';
import '../Icons/ArchiveIcon.js';
import '../Icons/ArrowDownIcon.js';
import '../Icons/ArrowLeftBottomIcon.js';
import '../Icons/ArrowLeftIcon.js';
import '../Icons/ArrowLeftTopIcon.js';
import '../Icons/ArrowRightBottomIcon.js';
import '../Icons/ArrowRightIcon.js';
import '../Icons/ArrowRightTopIcon.js';
import '../Icons/BagIcon.js';
import '../Icons/BasketIcon.js';
import '../Icons/BellFilledIcon.js';
import '../Icons/BellNotificationIcon.js';
import '../Icons/BellOutlineIcon.js';
import '../Icons/BinocularsIcon.js';
import '../Icons/BlocksIcon.js';
import '../Icons/BoltIcon.js';
import '../Icons/BookIcon.js';
import '../Icons/BookmarkAddFilledIcon.js';
import '../Icons/BookmarkAddOutlineIcon.js';
import '../Icons/BookmarkIcon.js';
import '../Icons/BookOpenedIcon.js';
import '../Icons/BoxAltIcon.js';
import '../Icons/BusinessIcon.js';
import '../Icons/CalendarIcon.js';
import '../Icons/CancelIcon.js';
import '../Icons/CardsIcon.js';
import '../Icons/ChatAltIcon.js';
import '../Icons/ChatIcon.js';
import '../Icons/CheckboxIcon.js';
import '../Icons/CheckCirlcleFilledIcon.js';
import '../Icons/CheckSquareIcon.js';
import '../Icons/ChevronDoubleLeftIcon.js';
import '../Icons/ChevronDoubleRightIcon.js';
import { ChevronDownIcon } from '../Icons/ChevronDownIcon.js';
import '../Icons/ChevronLeftIcon.js';
import '../Icons/ChevronRightIcon.js';
import { ChevronUpIcon } from '../Icons/ChevronUpIcon.js';
import '../Icons/ClipboardEditIcon.js';
import '../Icons/ClipboardIcon.js';
import '../Icons/CloseIcon.js';
import '../Icons/CollapseIcon.js';
import '../Icons/ControlsIcon.js';
import '../Icons/ControlsVerticalAltIcon.js';
import '../Icons/CopyIcon.js';
import '../Icons/CutIcon.js';
import '../Icons/DangerIcon.js';
import '../Icons/DownloadCloudIcon.js';
import '../Icons/DownloadIcon.js';
import '../Icons/DownloadPdfIcon.js';
import '../Icons/DrawerOpenIcon.js';
import '../Icons/EllipsisIcon.js';
import '../Icons/EllypsisVerticalIcon.js';
import '../Icons/EnvelopeIcon.js';
import '../Icons/ErrorIcon.js';
import '../Icons/ExitIcon.js';
import '../Icons/ExpandIcon.js';
import '../Icons/ExternalLinkIcon.js';
import '../Icons/EyeCrossedIcon.js';
import '../Icons/EyeIcon.js';
import '../Icons/FacebookIcon.js';
import '../Icons/FavouriteIcon.js';
import '../Icons/FileBadgeIcon.js';
import '../Icons/FileIcon.js';
import '../Icons/FileSearchIcon.js';
import '../Icons/FileType2Icon.js';
import '../Icons/FilmIcon.js';
import '../Icons/FitscreenIcon.js';
import '../Icons/FocusIcon.js';
import '../Icons/FolderAddIcon.js';
import '../Icons/FolderClosedIcon.js';
import '../Icons/FolderIcon.js';
import '../Icons/FutureIcon.js';
import '../Icons/GalleryVerticalEndIcon.js';
import '../Icons/GridIcon.js';
import '../Icons/HelpIcon.js';
import '../Icons/HistoryIcon.js';
import '../Icons/HomeIcon.js';
import '../Icons/InformationIcon.js';
import '../Icons/InstagramIcon.js';
import '../Icons/LibraryIcon.js';
import '../Icons/LightbulbIcon.js';
import '../Icons/LinkedinIcon.js';
import '../Icons/ListIcon.js';
import '../Icons/ListSearchIcon.js';
import '../Icons/Loader2Icon.js';
import '../Icons/LockAltIcon.js';
import '../Icons/LockAltOpenIcon.js';
import '../Icons/LockIcon.js';
import '../Icons/LockOpenIcon.js';
import '../Icons/MagazineIcon.js';
import '../Icons/MailIcon.js';
import '../Icons/Maximize2Icon.js';
import '../Icons/MenuCollapsable2Icon.js';
import '../Icons/MenuCollapsableIcon.js';
import '../Icons/MenuDotsIcon.js';
import '../Icons/MenuIcon.js';
import '../Icons/MinusIcon.js';
import '../Icons/NewsIcon.js';
import '../Icons/NtfrIcon.js';
import '../Icons/OkCircleIcon.js';
import '../Icons/OkIcon.js';
import '../Icons/PencilIcon.js';
import '../Icons/PenSquareIcon.js';
import '../Icons/People2Icon.js';
import '../Icons/PeopleIcon.js';
import '../Icons/PersonIcon.js';
import '../Icons/PhoneCallIcon.js';
import '../Icons/PlayFilledIcon.js';
import '../Icons/PlayIcon.js';
import '../Icons/PlusCircleIcon.js';
import '../Icons/PlusIcon.js';
import '../Icons/PlusSquareIcon.js';
import '../Icons/PrintIcon.js';
import '../Icons/RadioIcon.js';
import '../Icons/RefreshCwIcon.js';
import '../Icons/RotateIcon.js';
import '../Icons/SearchIcon.js';
import '../Icons/SettingsIcon.js';
import '../Icons/ShareIcon.js';
import '../Icons/ShieldIcon.js';
import '../Icons/SocialXIcon.js';
import '../Icons/StarFilledIcon.js';
import '../Icons/StarOutlineIcon.js';
import '../Icons/StickyNoteIcon.js';
import '../Icons/SwapHorizontalIcon.js';
import '../Icons/TablePropertiesIcon.js';
import '../Icons/TasklistIcon.js';
import '../Icons/ThumbDownIcon.js';
import '../Icons/ThumbsDownIcon.js';
import '../Icons/ThumbsUpIcon.js';
import '../Icons/ThumbUpIcon.js';
import '../Icons/TimeIcon.js';
import '../Icons/TimerIcon.js';
import '../Icons/Trash2Icon.js';
import '../Icons/TrashIcon.js';
import '../Icons/UpgradeIcon.js';
import '../Icons/UploadIcon.js';
import '../Icons/UserIcon.js';
import '../Icons/VideoIcon.js';
import '../Icons/WorldIcon.js';
import '../Icons/XposiIcon.js';
import '../Icons/YoutubeIcon.js';
import { useTable } from './TableProvider.js';
import { Th } from './Th.js';
import { Tr } from './Tr.js';

/**
 * @param column
 * @returns {[currentAriaDirection, nextDirection]}
 */
const getSortingDir = column => {
  const canSort = column.getCanSort();
  if (!canSort) {
    return ['none', false]; // sorting disabled
  }
  const isSorted = column.getIsSorted();
  switch (isSorted) {
    case 'asc':
      return ['ascending', 'desc'];
    case 'desc':
      return ['descending', 'asc'];
    default:
      return ['none', column.getFirstSortDir()];
  }
};
const StyledArrowDownIcon = styled(ChevronDownIcon).withConfig({
  shouldForwardProp: (prop, defaultValidationFn) => defaultValidationFn(prop)
}).withConfig({
  componentId: "sc-9f7guw-0"
})(["position:relative;top:-1px;margin-left:", ";vertical-align:middle;color:", ";", ""], ({
  theme
}) => theme.components.table.iconMargin, ({
  theme
}) => theme.components.table.sortInActiveColor, ({
  isActive
}) => isActive && css(["color:", ";"], ({
  theme
}) => theme.components.table.sortActiveColor));
const StyledArrowUpIcon = styled(ChevronUpIcon).withConfig({
  shouldForwardProp: (prop, defaultValidationFn) => defaultValidationFn(prop)
}).withConfig({
  componentId: "sc-9f7guw-1"
})(["position:relative;top:-1px;margin-left:", ";vertical-align:middle;color:", ";", ""], ({
  theme
}) => theme.components.table.iconMargin, ({
  theme
}) => theme.components.table.sortInActiveColor, ({
  isActive
}) => isActive && css(["color:", ";"], ({
  theme
}) => theme.components.table.sortActiveColor));
const THead = styled.thead.withConfig({
  componentId: "sc-9f7guw-2"
})(["", " color:", ";text-align:left;&:first-child{border-bottom:", ";}"], textStyle('labelBold'), ({
  theme
}) => theme.components.table.headerFontColor, ({
  theme
}) => theme.components.table.rowBorderBottomFirst);
const getSortingElement = header => {
  const sortOptions = {
    desc: jsx(StyledArrowDownIcon, {
      "data-test-id": "tableSortAscIcon",
      isActive: true
    }),
    asc: jsx(StyledArrowUpIcon, {
      "data-test-id": "tableSortDescIcon",
      isActive: true
    })
  };
  const sorting = header.column.getIsSorted();
  return typeof sorting === 'string' ? sortOptions[sorting] : null;
};
const TableHead = /*#__PURE__*/withTitan(() => {
  const {
    instance,
    hasRowSelection,
    labelColumnSort = ({
      columnHeader,
      sortDirection
    }) => {
      const sortDirectionLabel = sortDirection === 'asc' ? 'oplopend' : 'aflopend';
      if (!columnHeader) return `Sorteer ${sortDirectionLabel}`;
      return `Sorteer "${columnHeader}" ${sortDirectionLabel}`;
    }
  } = useTable();
  const {
    getHeaderGroups
  } = instance;
  const handleKeydown = useCallback((event, header) => {
    if (header.column.getCanSort() && (event.key === 'Enter' || event.key === ' ')) {
      header.column.getToggleSortingHandler();
    }
  }, []);
  return jsx(THead, {
    children: getHeaderGroups().map((headerGroup, index) => jsx(Tr, {
      children: headerGroup.headers.map(header => {
        const headerElement = flexRender(header.column.columnDef.header, header.getContext());
        const [currentAriaDir, nextDir] = getSortingDir(header.column);
        const title = nextDir ? t(labelColumnSort, {
          columnHeader: typeof headerElement === 'string' ? headerElement : '',
          sortDirection: nextDir
        }) : undefined;
        return jsxs(Th, {
          "aria-sort": currentAriaDir,
          canSort: header.column.getCanSort(),
          colSize: header.column.getSize(),
          isCheckboxColumn: hasRowSelection && index === 0,
          tabIndex: header.column.getCanSort() ? 0 : undefined,
          title: title,
          onClick: header.column.getToggleSortingHandler(),
          onKeyDown: e => handleKeydown(e, header),
          children: [headerElement, getSortingElement(header)]
        }, header.id);
      })
    }, headerGroup.id))
  });
}, {
  name: 'TableHead'
});

export { TableHead };
//# sourceMappingURL=TableHead.js.map
